import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/feature/Pagination';
import { useSeo } from '@/hooks/useSeo';
import { CATEGORY_META, PAGE_SIZE, REGIONS, YEARS, filterMovies } from '@/utils/media';

const TYPE_TABS = [
  { key: 'hot', label: '全部', to: '/category/hot' },
  { key: 'movie', label: '电影', to: '/category/movie' },
  { key: 'tv', label: '电视剧', to: '/category/tv' },
];

const SORTS = [
  { key: 'time', label: '按时间' },
  { key: 'hot', label: '按热度' },
  { key: 'rating', label: '按评分' },
];

export default function CategoryPage() {
  const { type = 'hot' } = useParams();
  const meta = CATEGORY_META[type] ?? CATEGORY_META.hot;

  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState('time');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useSeo({
    title: `${meta.title}分类 - 91爱爱影视平台`,
    description: `91爱爱${meta.title}分类页，${meta.desc}支持按地区、年份筛选与热度、时间排序。`,
    keywords: `91爱爱,${meta.title},影视分类,${meta.title}资源,影视推荐`,
  });

  useEffect(() => {
    setPage(1);
  }, [type, region, year, sort]);

  useEffect(() => {
    setRegion('全部');
    setYear('全部');
    setSort('time');
  }, [type]);

  const filtered = useMemo(
    () => filterMovies({ type, region, year, sort }),
    [type, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setRegion('全部');
    setYear('全部');
    setSort('time');
    setPage(1);
  };

  const chipClass = (active: boolean) =>
    `whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors cursor-pointer ${
      active
        ? 'border-primary-500 bg-primary-600 text-background-50'
        : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
    }`;

  const FilterGroup = () => (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-xs font-medium text-foreground-500">地区</p>
        <div className="flex flex-wrap gap-2">
          {REGIONS.map((item) => (
            <button key={item} type="button" onClick={() => setRegion(item)} className={chipClass(region === item)}>
              {item}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-medium text-foreground-500">年份</p>
        <div className="flex flex-wrap gap-2">
          {YEARS.map((item) => (
            <button key={item} type="button" onClick={() => setYear(item)} className={chipClass(year === item)}>
              {item}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-medium text-foreground-500">排序</p>
        <div className="flex flex-wrap gap-2">
          {SORTS.map((item) => (
            <button key={item.key} type="button" onClick={() => setSort(item.key)} className={chipClass(sort === item.key)}>
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6">
        <Breadcrumb items={[{ label: '影视分类', to: '/category/hot' }, { label: meta.title }]} />
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6">
        <div className="rounded-lg border border-background-200 bg-primary-50/60 p-5 md:p-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {meta.title}分类
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{meta.desc}</p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {TYPE_TABS.map((tab) => (
              <Link
                key={tab.key}
                to={tab.to}
                className={`whitespace-nowrap rounded-md border px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  type === tab.key
                    ? 'border-primary-600 bg-primary-600 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {tab.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-6 lg:hidden">
          <button
            type="button"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="flex h-11 w-full items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 text-sm font-medium text-foreground-700 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-primary-600" />
              筛选与排序
            </span>
            <i className={`${filtersOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`} />
          </button>
          {filtersOpen && (
            <div className="mt-3 rounded-lg border border-background-200 bg-background-50 p-4">
              <FilterGroup />
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="mt-4 h-10 w-full whitespace-nowrap rounded-md bg-primary-600 text-sm font-medium text-background-50 cursor-pointer"
              >
                查看结果（{filtered.length}）
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[240px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-equalizer-2-line text-primary-600" />
                  筛选条件
                </h2>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="whitespace-nowrap text-xs text-foreground-400 transition-colors hover:text-primary-600 cursor-pointer"
                >
                  重置
                </button>
              </div>
              <FilterGroup />
            </div>
          </aside>

          <div className="min-w-0">
            <div className="mb-4 flex items-center justify-between text-xs text-foreground-500">
              <span>
                共找到 <strong className="text-primary-600">{filtered.length}</strong> 部影视作品
              </span>
              <span className="hidden sm:inline">
                第 {page} / {totalPages} 页
              </span>
            </div>

            {current.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {current.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                <Pagination page={page} total={totalPages} onChange={setPage} />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20">
                <i className="ri-emotion-unhappy-line text-5xl text-foreground-300" />
                <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">
                  暂无符合条件的影视资源
                </p>
                <p className="mt-1 text-sm text-foreground-500">试试更换地区或年份筛选条件</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 h-10 whitespace-nowrap rounded-md bg-primary-600 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
                >
                  清除筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}